import React from 'react';
import './PrivacyPolicy.css';

export default function PrivacyPolicy() {
  return (
    <div className="privacy-policy">
      <div className="privacy-policy__hero">
        <div className="container">
          <h1>Privacy Policy</h1>
          <p className="privacy-policy__date">Last Updated: December 1, 2024</p>
        </div>
      </div>

      <div className="container privacy-policy__content">
        <div className="privacy-policy__contact-box">
          <h3>Privacy Officer</h3>
          <p><strong>Christophe Lavoie</strong></p>
          <p>Email: <a href="mailto:privacy@vision-security.ca">privacy@vision-security.ca</a></p>
          <p>Phone: <a href="tel:+15142797209">(514) 279-7209</a></p>
          <p>Address: 3285 Boul. Cavendish, Suite 620, Montréal, Québec H4B 2L9, Canada</p>
          <p className="privacy-policy__response-time">We respond to all privacy inquiries and requests within 30 days of receipt.</p>
        </div>

        <section>
          <h2>1. Introduction</h2>
          <p>
            Vision Sécurité Inc. ("Vision Sécurité," "we," "us," or "our") is committed to protecting the privacy and 
            personal information of our clients, website visitors, employees, and business partners. This Privacy Policy 
            explains how we collect, use, disclose, retain, and safeguard personal information in compliance with Quebec's 
            <strong> Act respecting the protection of personal information in the private sector</strong> (as amended by Law 25) 
            and Canada's <strong>Personal Information Protection and Electronic Documents Act (PIPEDA)</strong>. Where applicable, 
            we also apply best practices aligned with the European Union's General Data Protection Regulation (GDPR) to ensure 
            robust privacy protection.
          </p>
          <p>
            This Policy applies to all personal information processed through our websites, security services, business 
            operations, and offline interactions. Our commitment to transparency means you can understand exactly what 
            information we collect, why we collect it, and what rights you have over your data.
          </p>
        </section>

        <section>
          <h2>2. Personal Information We Collect</h2>
          <p>
            We collect only the personal information necessary to deliver professional security services and maintain our 
            business operations. The types of information we collect depend on your relationship with us and include:
          </p>
          
          <h3>Identity and Contact Information</h3>
          <p>
            This includes names, job titles, business and residential addresses, telephone numbers, and email addresses of 
            clients, employees, job candidates, emergency contacts, and business partners. We collect this information to 
            communicate effectively, fulfill contracts, and maintain professional relationships.
          </p>

          <h3>Client and Service-Related Data</h3>
          <p>
            To provide tailored security solutions, we gather site-specific details such as property layouts, access 
            instructions, alarm codes, emergency contact lists, and standard operating procedures. This information is 
            essential for our security personnel to perform their duties effectively and respond appropriately to incidents.
          </p>

          <h3>Operational and Security Data</h3>
          <p>
            During the course of our security operations, we create and maintain patrol logs, time-stamped incident reports, 
            photographs or video recordings captured during security patrols or incident response, access control records 
            showing entry and exit times, and visitor logs. This data serves both operational and evidentiary purposes and 
            may be retained to support insurance claims or legal proceedings.
          </p>

          <h3>HR and Employment Information</h3>
          <p>For current employees and job candidates, we collect:</p>
          <ul>
            <li>Resumes and applications</li>
            <li>Copies of BSP (Bureau de la sécurité privée) licenses and professional certifications</li>
            <li>Work schedules and time records</li>
            <li>Performance evaluations</li>
            <li>Training completion records</li>
            <li>Payroll and benefits information</li>
            <li>Background check results where permitted by law</li>
          </ul>

          <h3>Website and Technical Data</h3>
          <p>When you visit our website, we automatically collect certain technical information including:</p>
          <ul>
            <li>IP addresses</li>
            <li>Device type and browser information</li>
            <li>Pages viewed and time spent on each page</li>
            <li>Referral sources (how you found our site)</li>
            <li>Cookie preferences</li>
            <li>Approximate geographic location based on IP address when enabled by your device settings</li>
          </ul>

          <h3>Marketing and Communications</h3>
          <p>We maintain records of:</p>
          <ul>
            <li>Your consent preferences for marketing communications</li>
            <li>Event registrations and attendance</li>
            <li>Feedback and survey responses</li>
            <li>Correspondence history with our sales and customer support teams</li>
          </ul>
        </section>

        <section>
          <h2>3. How We Collect Information</h2>
          <p>We collect personal information through multiple channels to serve you effectively:</p>

          <h3>Direct Interactions</h3>
          <p>Most personal information comes directly from you when you:</p>
          <ul>
            <li>Complete online or paper forms</li>
            <li>Sign service contracts or employment agreements</li>
            <li>Communicate with us via email or telephone</li>
            <li>Meet with our representatives in person</li>
            <li>Subscribe to our newsletters or event invitations</li>
            <li>Apply for employment positions</li>
          </ul>

          <h3>Service Delivery Activities</h3>
          <p>Our security personnel collect information during:</p>
          <ul>
            <li>Patrol activities</li>
            <li>Incident response and documentation</li>
            <li>Video surveillance or photographic documentation conducted as part of authorized security operations</li>
            <li>Access control monitoring at client sites</li>
          </ul>

          <h3>Third-Party Sources</h3>
          <p>
            In some cases, business clients or property managers lawfully provide us with information about their premises, 
            tenants, or authorized personnel to enable us to fulfill our contractual security obligations. We also obtain 
            information from publicly available sources and government licensing bodies such as the Bureau de la sécurité 
            privée (BSP) to verify credentials and maintain compliance.
          </p>

          <h3>Automated Technologies</h3>
          <p>
            Our website uses cookies, web beacons, and similar technologies to collect usage information. We explain these 
            technologies in detail in Section 10 below.
          </p>

          <h3>Service Providers</h3>
          <p>
            Certain trusted third-party service providers assist our operations under strict confidentiality agreements and 
            may collect information on our behalf, such as background check agencies, payroll processors, and IT infrastructure 
            providers.
          </p>
        </section>

        <section>
          <h2>4. Purposes and Legal Bases for Processing</h2>
          <p>
            We process personal information only for specific, legitimate purposes and rely on appropriate legal bases as 
            required by privacy law:
          </p>

          <h3>Service Delivery</h3>
          <p>Our primary purpose is to provide contracted security services including:</p>
          <ul>
            <li>Site assessments and risk evaluations</li>
            <li>Deployment and management of security personnel</li>
            <li>Patrol services and perimeter monitoring</li>
            <li>Incident response and emergency coordination</li>
            <li>Reporting and client communications</li>
            <li>Invoice processing and account management</li>
          </ul>
          <p>
            The legal basis for this processing is the performance of our contractual obligations to clients and the 
            legitimate interests in delivering quality services.
          </p>

          <h3>Legal and Regulatory Compliance</h3>
          <p>We process personal information to comply with:</p>
          <ul>
            <li>Licensing requirements under Quebec's Private Security Act</li>
            <li>Employment standards and labor law obligations</li>
            <li>Tax reporting and remittance</li>
            <li>Workplace health and safety regulations</li>
            <li>Incident reporting as mandated by law or client contracts</li>
            <li>Responses to lawful requests from law enforcement or regulatory authorities</li>
          </ul>
          <p>The legal basis for this processing is compliance with legal obligations.</p>

          <h3>Business Operations</h3>
          <p>To maintain and improve our operations, we use personal information for:</p>
          <ul>
            <li>Quality assurance and service improvement</li>
            <li>Employee training and performance management</li>
            <li>Scheduling and resource allocation</li>
            <li>Billing, accounting, and financial audits</li>
            <li>Cybersecurity and information security measures</li>
            <li>Business continuity planning</li>
          </ul>
          <p>The legal basis is our legitimate business interests balanced against your privacy rights.</p>

          <h3>Business Development and Marketing</h3>
          <p>We process personal information for:</p>
          <ul>
            <li>Sales and business development activities</li>
            <li>Proposal and bid preparation</li>
            <li>Customer support and relationship management</li>
            <li>Marketing communications where you have provided appropriate consent</li>
          </ul>
          <p>For marketing purposes, we rely on your explicit consent, which you may withdraw at any time.</p>

          <h3>Protection of Vital Interests</h3>
          <p>
            In emergency situations, we may process personal information to protect the vital interests of individuals, such 
            as contacting emergency services or providing critical information to first responders.
          </p>
        </section>

        <section>
          <h2>5. Consent</h2>
          <p>We obtain free, informed, and specific consent where required by law, particularly for:</p>
          <ul>
            <li>Marketing communications</li>
            <li>Non-essential website cookies and tracking technologies</li>
            <li>Certain disclosures of personal information beyond what is necessary for service delivery</li>
          </ul>

          <h3>Your Right to Withdraw Consent</h3>
          <p>
            You may withdraw your consent at any time by contacting our Privacy Officer. Please note that withdrawing consent 
            may affect our ability to provide certain services or communications. For example, if you withdraw consent for 
            marketing emails, you will no longer receive promotional offers, though we will continue to send transactional 
            communications related to your account or services.
          </p>

          <h3>Consent for Minors</h3>
          <p>
            We do not knowingly collect personal information directly from children under 14 years of age without obtaining 
            consent from a parent or legal guardian, unless specifically permitted by applicable law. If we become aware that 
            we have inadvertently collected such information, we will take steps to delete it promptly.
          </p>
        </section>

        <section>
          <h2>6. Sharing and Disclosure</h2>
          <p>
            <strong>We do not sell personal information under any circumstances.</strong> We may share personal information 
            with the following categories of recipients under appropriate safeguards:
          </p>

          <h3>Service Providers and Contractors</h3>
          <p>We engage trusted third-party service providers who assist our operations under strict confidentiality commitments, including:</p>
          <ul>
            <li>IT and cloud infrastructure providers</li>
            <li>Background check and verification services</li>
            <li>Payroll and benefits processors</li>
            <li>Professional training and certification organizations</li>
            <li>Emergency dispatch and coordination services</li>
          </ul>
          <p>
            These providers are contractually obligated to protect personal information and use it only for the specific 
            purposes we authorize.
          </p>

          <h3>Professional Advisors</h3>
          <p>We share necessary information with:</p>
          <ul>
            <li>Legal counsel</li>
            <li>Accountants and auditors</li>
            <li>Insurance providers and brokers</li>
          </ul>

          <h3>Law Enforcement and Regulatory Authorities</h3>
          <p>We disclose personal information to:</p>
          <ul>
            <li>Police, law enforcement agencies, and public safety officials</li>
            <li>Regulatory bodies including the Bureau de la sécurité privée and the Commission d'accès à l'information</li>
            <li>Courts and tribunals</li>
            <li>Other public authorities when required or permitted by law</li>
          </ul>

          <h3>Corporate Transactions</h3>
          <p>
            In connection with a merger, acquisition, sale of assets, or similar corporate transaction, personal information 
            may be transferred to another organization. We will ensure appropriate confidentiality safeguards are in place and, 
            where required, provide notice and obtain consent for such transfers.
          </p>
        </section>

        <section>
          <h2>7. Individual Rights</h2>
          <p>Subject to applicable legal requirements and exceptions, you have the following rights regarding your personal information:</p>

          <div className="rights-grid">
            <div className="right-item">
              <h3>Right of Access</h3>
              <p>
                You may request copies of the personal information we hold about you and receive details about how it is used, 
                with whom it is shared, and how long it will be retained. We will respond to access requests within 30 days and 
                provide the information in an understandable format.
              </p>
            </div>

            <div className="right-item">
              <h3>Right to Rectification</h3>
              <p>
                If you believe personal information we hold about you is incomplete, inaccurate, or outdated, you have the right 
                to request corrections. We will make reasonable efforts to verify and update the information promptly.
              </p>
            </div>

            <div className="right-item">
              <h3>Right to Erasure</h3>
              <p>
                Where permitted by law and after fulfilling legal retention obligations, you may request deletion of your personal 
                information. Please note that certain information must be retained for legal, contractual, or legitimate business 
                purposes, such as records required for tax compliance or ongoing legal proceedings.
              </p>
            </div>

            <div className="right-item">
              <h3>Right to Data Portability</h3>
              <p>
                You have the right to receive certain personal information in a structured, commonly used, and machine-readable 
                format for transmission to another service provider. This right applies to information you have provided to us 
                with your consent or for contract performance.
              </p>
            </div>

            <div className="right-item">
              <h3>Right to Object or Restrict Processing</h3>
              <p>
                You may object to or request restrictions on certain types of processing, particularly for direct marketing 
                purposes. We will honor such requests unless we have compelling legitimate grounds to continue processing.
              </p>
            </div>

            <div className="right-item">
              <h3>De-indexing and Cessation of Dissemination</h3>
              <p>
                Quebec's Law 25 provides specific rights to request de-indexing or cessation of dissemination of your personal 
                information in certain circumstances, such as when information is outdated, incomplete, or no longer necessary 
                for the purposes for which it was collected.
              </p>
            </div>

            <div className="right-item">
              <h3>Automated Decision-Making</h3>
              <p>
                For any automated decision-making processes that produce legal effects or similarly significant effects on you, 
                you have the right to receive information about the processing, challenge the decision, and present your 
                observations. Currently, Vision Sécurité does not engage in fully automated decision-making with legal or 
                significant effects.
              </p>
            </div>
          </div>

          <div className="exercise-rights-box">
            <h3>Exercising Your Rights</h3>
            <p>
              To exercise any of these rights, please submit a written request to our Privacy Officer at 
              <a href="mailto:privacy@vision-security.ca"> privacy@vision-security.ca</a> or 
              <a href="tel:+15142797209"> (514) 279-7209</a>. We will respond within 30 days of receiving your request. 
              We may require proof of identity to protect against unauthorized access to your personal information.
            </p>
          </div>
        </section>

        <section>
          <h2>8. Security Measures</h2>
          <p>
            We implement comprehensive technical, administrative, and physical safeguards proportionate to the risks associated 
            with processing personal information:
          </p>

          <h3>Technical Safeguards</h3>
          <p>Our security measures include:</p>
          <ul>
            <li>Encryption of data in transit and at rest where applicable using industry-standard protocols</li>
            <li>Multi-factor authentication systems for access to sensitive systems</li>
            <li>Role-based access controls ensuring employees access only information necessary for their roles</li>
            <li>Secure system configuration with regular security updates and patches</li>
            <li>Intrusion detection and prevention systems</li>
            <li>Regular vulnerability assessments and penetration testing</li>
            <li>Secure backup and disaster recovery procedures</li>
          </ul>

          <h3>Administrative Safeguards</h3>
          <p>We maintain strict internal controls including:</p>
          <ul>
            <li>Role-based permission structures</li>
            <li>Confidentiality and non-disclosure agreements for all personnel with access to personal information</li>
            <li>Regular privacy and security training for employees</li>
            <li>Vendor security assessments and ongoing monitoring of third-party practices</li>
            <li>Incident response plans and breach notification procedures</li>
            <li>Regular privacy impact assessments for new projects</li>
          </ul>

          <h3>Physical Safeguards</h3>
          <p>Our physical security measures include:</p>
          <ul>
            <li>Secured office facilities with controlled access and visitor management</li>
            <li>Locked storage for physical documents containing personal information</li>
            <li>Secure destruction procedures including cross-cut shredding for paper documents and certified electronic data wiping for digital media</li>
            <li>Restricted access to server rooms and data storage facilities</li>
          </ul>

          <div className="security-disclaimer">
            <h3>Limitation of Security</h3>
            <p>
              While we implement industry-standard security measures and continuously work to improve our safeguards, no system 
              is completely secure. Despite our best efforts, some residual risk may remain. We cannot guarantee absolute security 
              but commit to promptly addressing any identified vulnerabilities and notifying affected individuals of any breaches 
              that pose a risk of serious harm.
            </p>
          </div>
        </section>

        <section>
          <h2>9. Retention and Destruction</h2>
          <p>We retain personal information only as long as necessary to fulfill the stated purposes and meet legal requirements:</p>

          <div className="retention-table">
            <div className="retention-row">
              <div className="retention-type"><strong>Client Records and Contracts</strong></div>
              <div className="retention-period">Typically retained for seven (7) years following the end of the contract to comply with commercial and tax law requirements.</div>
            </div>
            <div className="retention-row">
              <div className="retention-type"><strong>Security and Incident Reports</strong></div>
              <div className="retention-period">May be retained for up to ten (10) years to support insurance claims, legal proceedings, and regulatory requirements.</div>
            </div>
            <div className="retention-row">
              <div className="retention-type"><strong>Employee and HR Records</strong></div>
              <div className="retention-period">Generally maintained for seven (7) years after the end of employment to comply with employment standards, tax, and pension legislation.</div>
            </div>
            <div className="retention-row">
              <div className="retention-type"><strong>Website Analytics Data</strong></div>
              <div className="retention-period">Retained for up to twenty-six (26) months in accordance with privacy best practices for analytics cookies.</div>
            </div>
          </div>

          <h3>Secure Destruction</h3>
          <p>When personal information is no longer required for business or legal purposes, it is securely destroyed through approved methods including:</p>
          <ul>
            <li>Cross-cut shredding of physical documents</li>
            <li>Certified electronic data wiping or destruction of storage media</li>
            <li>De-identification or anonymization where data may be retained for statistical or research purposes in a form that does not identify individuals</li>
          </ul>
        </section>

        <section>
          <h2>10. Cookies and Tracking</h2>
          <p>Our website uses different types of cookies and similar tracking technologies:</p>

          <div className="cookie-notice-banner">
            <h3>⚠️ Important Notice</h3>
            <p>
              Our cookie consent banner is currently not functional. We are actively working to resolve this issue. 
              In the meantime, you can manage your cookie preferences directly through your browser settings.
            </p>
          </div>

          <h3>Essential Cookies</h3>
          <p>
            These cookies are required for basic website functionality such as page navigation, secure access to certain areas, 
            and maintaining your session. Essential cookies cannot be disabled through our cookie banner as they are necessary 
            for the website to function. Examples include session identifiers and security tokens.
          </p>

          <h3>Analytics and Performance Cookies</h3>
          <p>With your consent, we use analytics cookies to:</p>
          <ul>
            <li>Understand how visitors interact with our website</li>
            <li>Identify popular content and navigation patterns</li>
            <li>Detect technical issues and improve website performance</li>
            <li>Measure the effectiveness of our marketing campaigns</li>
          </ul>
          <p>We may use services such as Google Analytics for these purposes.</p>

          <h3>Marketing Cookies</h3>
          <p>These cookies require your explicit consent and allow us to:</p>
          <ul>
            <li>Deliver personalized content and advertisements</li>
            <li>Track the effectiveness of advertising campaigns</li>
            <li>Limit the number of times you see an advertisement</li>
            <li>Measure user engagement with marketing materials</li>
          </ul>

          <h3>Managing Cookie Preferences</h3>
          <p>
            You can manage your cookie preferences through your browser settings until our cookie banner is fixed. 
            Please note that disabling certain cookies may limit website functionality and your user experience. 
            Blocking essential cookies will prevent you from accessing certain areas or features of the website.
          </p>

          <h3>Third-Party Cookies</h3>
          <p>
            Some cookies may be set by third-party services that appear on our pages. We do not control these third-party 
            cookies and recommend reviewing the privacy policies of these third parties for information about their data practices.
          </p>
        </section>

        <section>
          <h2>11. International Transfers</h2>
          <p>
            Vision Sécurité primarily operates within Quebec and Canada. However, some of our service providers may process 
            personal information outside of Quebec or Canada, including in jurisdictions that may not provide the same level 
            of data protection.
          </p>
          <p>When personal information must be transferred outside Quebec or Canada, we implement appropriate safeguards including:</p>
          <ul>
            <li>Standard contractual clauses approved by privacy regulators</li>
            <li>Data processing agreements with strict security and confidentiality obligations</li>
            <li>Privacy impact assessments to evaluate risks associated with the transfer</li>
            <li>Ongoing oversight and audits of third-party processors</li>
          </ul>
          <p>
            We remain fully responsible and accountable for personal information entrusted to service providers regardless of 
            their geographic location. If you have concerns about international transfers of your information, please contact 
            our Privacy Officer.
          </p>
        </section>

        <section>
          <h2>12. Privacy Incidents and Breach Notification</h2>
          <p>
            We maintain a confidentiality incident register and conduct thorough risk assessments following any privacy incident 
            to determine the likelihood and severity of harm to affected individuals.
          </p>

          <h3>Breach Response</h3>
          <p>
            In the event of a privacy breach that poses a risk of serious harm to affected individuals such as identity theft, 
            fraud, or damage to reputation or relationships, we will:
          </p>
          <ul>
            <li>Notify the Commission d'accès à l'information du Québec and, where applicable, the Office of the Privacy Commissioner of Canada as soon as feasible</li>
            <li>Notify affected individuals directly and without undue delay, providing information about the nature of the breach, the personal information involved, the potential consequences, and steps we are taking to mitigate harm</li>
            <li>Take immediate containment and remediation measures to prevent further unauthorized access or loss</li>
            <li>Conduct a post-incident review to identify root causes and implement corrective measures to prevent recurrence</li>
          </ul>

          <h3>Your Right to Notification</h3>
          <p>
            You have the right to be informed of any privacy breach that may cause you serious harm. We commit to transparent 
            and timely communication in such circumstances.
          </p>
        </section>

        <section>
          <h2>13. Children's Privacy</h2>
          <p>
            Vision Sécurité does not knowingly collect personal information directly from children under fourteen (14) years 
            of age without obtaining proper consent from a parent or legal guardian, unless specifically permitted by applicable law.
          </p>
          <p>
            If we become aware that we have inadvertently collected personal information from a child under 14 without appropriate 
            parental consent, we will take prompt steps to delete the information from our systems. If you believe we have collected 
            information from a child improperly, please contact our Privacy Officer immediately.
          </p>
        </section>

        <section>
          <h2>14. Transparency and Impact Assessments</h2>
          <p>
            We conduct Privacy Impact Assessments (PIAs) where required by law for projects involving the acquisition, development, 
            or redesign of information systems or electronic service delivery that may affect the processing of personal information.
          </p>
          <p>These assessments help us identify and mitigate privacy risks before implementing new technologies or processes. Our PIAs evaluate:</p>
          <ul>
            <li>The sensitivity of personal information involved</li>
            <li>The purposes and legal bases for collection and use</li>
            <li>The quantity and distribution of data</li>
            <li>The storage medium and security measures</li>
            <li>The risks to individuals' privacy rights</li>
            <li>Mitigation strategies to reduce identified risks</li>
          </ul>
        </section>

        <section>
          <h2>15. Changes to This Policy</h2>
          <p>
            We may update this Privacy Policy periodically to reflect changes in our practices, technologies, legal requirements, 
            or organizational structure. When we make changes, we will update the "Last Updated" date at the top of this Policy.
          </p>

          <h3>Notice of Material Changes</h3>
          <p>For significant modifications that materially affect your rights or our practices, we will provide additional notice through:</p>
          <ul>
            <li>Prominent website announcements</li>
            <li>Direct email communication to clients and registered users</li>
            <li>Other appropriate means depending on the nature of the changes and our relationship with you</li>
          </ul>

          <h3>Your Responsibility</h3>
          <p>
            We encourage you to review this Privacy Policy periodically to stay informed about how we protect your personal 
            information. Continued use of our services after changes are posted constitutes acceptance of the updated Policy.
          </p>
        </section>

        <section>
          <h2>16. Contact and Complaints</h2>
          <p>For privacy-related inquiries, concerns, complaints, or to exercise your privacy rights, please contact:</p>

          <div className="contact-details-box">
            <p><strong>Privacy Officer:</strong> Christophe Lavoie</p>
            <p><strong>Email:</strong> <a href="mailto:privacy@vision-security.ca">privacy@vision-security.ca</a></p>
            <p><strong>Phone:</strong> <a href="tel:+15142797209">(514) 279-7209</a></p>
            <p><strong>Address:</strong> 3285 Boul. Cavendish, Suite 620, Montréal, Québec H4B 2L9, Canada</p>
            <p className="response-time"><strong>Response Time:</strong> We respond to all privacy requests within thirty (30) days of receipt.</p>
          </div>

          <h3>Regulatory Authorities</h3>
          <p>
            If you are not satisfied with our response to your privacy concern or complaint, you have the right to file a 
            complaint with regulatory authorities:
          </p>

          <div className="regulatory-boxes">
            <div className="regulatory-box">
              <h4>Quebec matters</h4>
              <p><strong>Commission d'accès à l'information du Québec</strong></p>
              <p>Website: <a href="https://www.cai.gouv.qc.ca" target="_blank" rel="noopener noreferrer">www.cai.gouv.qc.ca</a></p>
              <p>Phone: <a href="tel:+18885287741">1-888-528-7741</a></p>
            </div>

            <div className="regulatory-box">
              <h4>Federal jurisdiction matters</h4>
              <p><strong>Office of the Privacy Commissioner of Canada</strong></p>
              <p>Website: <a href="https://www.priv.gc.ca" target="_blank" rel="noopener noreferrer">www.priv.gc.ca</a></p>
              <p>Phone: <a href="tel:+18002821376">1-800-282-1376</a></p>
            </div>
          </div>

          <p className="regulatory-note">
            These regulators can investigate your complaint independently and have enforcement powers including the ability 
            to issue orders and impose penalties for non-compliance.
          </p>
        </section>
      </div>
    </div>
  );
}
